import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { globalEnv } from "~/global-env";
import { type ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { shouldShowTaxDeleteModal } from "~/pages/Compliance/CaseDetailsDrawer/Documents/utils/taxDocuments";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useIsFatcaCrsEnabledForCase } from "~/pages/Compliance/flags";
import { ComplianceDocumentType } from "../DocumentManagementList/types";
import { DocumentsViewPanel } from "./DocumentsViewPanel";

function getNextDocumentId(
  documents: { id: number }[],
  currentDocumentId: number,
): number | null {
  const remaining = documents.filter((d) => d.id !== currentDocumentId);
  if (remaining.length === 0) return null;
  const currentIndex = documents.findIndex((d) => d.id === currentDocumentId);
  const nextIndex = Math.min(Math.max(0, currentIndex), remaining.length - 1);
  return remaining[nextIndex].id;
}

interface Props {
  documents: ComplianceDocumentType[];
}

const DocumentManagementDisplay = ({ documents }: Props) => {
  const { caseId, caseType, investingProfileId, fatcaCrsEnabled } = useCurrentCaseContext();
  const isFatcaCrsEnabled = useIsFatcaCrsEnabledForCase(
    caseType as ComplianceCaseTypeEnum | undefined,
    fatcaCrsEnabled,
  );
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const firstDocument = documents[0];
  const documentIdStr = searchParams.get("documentId");
  // Radix 10 = parse as decimal (avoids octal interpretation for leading-zero strings)
  const documentId = documentIdStr ? parseInt(documentIdStr, 10) : firstDocument?.id;
  const currentDocument = documents.find((d) => d.id === documentId);
  const nextDocumentId =
    Number.isFinite(documentId) && firstDocument
      ? getNextDocumentId(documents, documentId)
      : null;

  const isEscalationRoute = location.pathname.startsWith(
    `${globalEnv.complianceRoute}/escalations`,
  );

  const afterDeletionCB = useCallback(() => {
    if (nextDocumentId != null) {
      const params = new URLSearchParams(location.search);
      params.set("documentId", String(nextDocumentId));
      const search = params.toString() ? `?${params.toString()}` : "";
      history.push({ pathname: location.pathname, search });
    } else {
      const targetPath = isEscalationRoute
        ? `${globalEnv.complianceRoute}/escalations/${caseId}/details`
        : `${globalEnv.complianceRoute}/case/${caseId}/documents`;
      history.push(targetPath);
    }
  }, [nextDocumentId, location, history, caseId, isEscalationRoute]);

  const deleteInfo = useMemo(
    () => ({
      showTaxDeleteModal: shouldShowTaxDeleteModal({
        isFatcaCrsEnabled,
        documentProfileId: currentDocument?.profileId,
        investingProfileId,
        documentBaseType: currentDocument?.documentPurpose?.baseType,
      }),
      afterDeletionCB,
    }),
    [
      isFatcaCrsEnabled,
      currentDocument?.profileId,
      currentDocument?.documentPurpose?.baseType,
      investingProfileId,
      afterDeletionCB,
    ],
  );

  return <DocumentsViewPanel caseId={caseId} documentId={documentId} deleteInfo={deleteInfo} />;
};

export default DocumentManagementDisplay;
